import React, { useState, useRef, useEffect } from "react";

/**
 * VerificationCodeInput - A 6-digit code input with individual squares
 * @param {Function} onChange - Callback when code changes (receives 6-digit string)
 * @param {string} value - Current code value
 * @param {boolean} disabled - Whether input is disabled
 */
const VerificationCodeInput = ({ onChange, value = "", disabled = false }) => {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Initialize digits from value prop
    if (value && value.length === 6) {
      setDigits(value.split(""));
    } else if (!value) {
      setDigits(["", "", "", "", "", ""]);
    }
  }, [value]);

  const handleChange = (index, newValue) => {
    // Only allow single digit
    if (newValue.length > 1) {
      return;
    }

    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) {
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = newValue;
    setDigits(newDigits);

    // Call onChange with complete code
    const code = newDigits.join("");
    if (onChange) {
      onChange(code);
    }

    // Auto-focus next input
    if (newValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Only accept 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const pastedDigits = pastedData.split("");
      setDigits(pastedDigits);
      if (onChange) {
        onChange(pastedData);
      }
      // Focus last input
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className="verification-code-container" onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          disabled={disabled}
          className="verification-code-input"
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;

